import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { OracleUsIndexSearchResponseDto, PtpIndexSearchResponseDto } from './search-response.dto';
import { Observable, map, of } from 'rxjs';
import { ADPResourceService } from 'digital-core';
import { EssItemGroup } from './ess-item-group';

type SelectListItem = { Key: string; Value: string };

@Injectable({
  providedIn: 'root'
})
export class EquipmentSearchService {

  private readonly apiVersion = '2021-04-30-Preview';
  private readonly ptpEquipmentIndexBaseUrl: string;
  private readonly apiKey;
  private readonly oracleUsEquipmentIndexBaseUrl: string;
  private cachedMakes: any;
  private cachedApplianceMakes?: SelectListItem[];
  private readonly cachedMakesByDeviceTypeCode = new Map<string, SelectListItem[]>();

  public constructor(
    private httpClient: HttpClient,
    private resourceService: ADPResourceService,
  ) {
    this.ptpEquipmentIndexBaseUrl = this.resourceService.getPortalSetting('URL_EquipmentSearchApi_BaseUrl').Value;
    this.apiKey = this.resourceService.getPortalSetting('URL_EquipmentSearchApi_Key').Value;
    this.oracleUsEquipmentIndexBaseUrl = this.resourceService.getPortalSetting('Global_Ess_OracleUsEquipment_Index_BaseUrl').Value;
  }

  public search(query: string) {
    const filterClause = this.resourceService.getPortaLabelValue('Global_Ess_PtpEquipment_Search_FilterClause');

    // parse incoming string and reconstruct with wildcard search at end of each word part
    let queryStr: string = '';
    const splitString = query.split(" ");
    if(splitString.length > 0){
      splitString.forEach((str: string) => {
        if(str.length>0){
          if(queryStr.length>0){
            queryStr += ' ';
          }
          queryStr += str.replace(/-/g, '\\-') +'*';
        }
      });
    }

    const url = `${this.ptpEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&${filterClause}&search=${queryStr?.trim() ?? ''}`;

    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<PtpIndexSearchResponseDto>(url, { headers: headers });
  }

  public searchByDeviceType(query: string, deviceType) {
    const filterClause = '$filter=Attributes/DeviceTypeCode eq \'' + deviceType + '\'';

    // parse incoming string and reconstruct with wildcard search at end of each word part
    let queryStr: string = '';
    const splitString = query.split(" ");
    if(splitString.length > 0){
      splitString.forEach((str: string) => {
        if(str.length>0){
          if(queryStr.length>0){
            queryStr += ' ';
          }
          queryStr += str.replace(/-/g, '\\-') +'*';
        }
      });
    }

    const url = `${this.ptpEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&${filterClause}&search=${queryStr?.trim() ?? ''}`;

    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<PtpIndexSearchResponseDto>(url, { headers: headers });
  }

  public searchByDeviceTypeOracle(query: string, deviceTypes: Array<number | string | undefined | null>): Observable<OracleUsIndexSearchResponseDto> {
    let filterClause = '';
    const deviceTypesValue = deviceTypes?.filter(x => x !== null && x !== undefined);

    if (deviceTypesValue?.length > 0) {
      const filterParts = deviceTypesValue.map(x => `Attributes/DeviceTypeCode eq '${x}'`);
      filterClause = `&$filter=(${filterParts.join(' or ')})`;
    }

    // parse incoming string and reconstruct with wildcard search at end of each word part
    let queryStr: string = '';
    const splitString = query.split(' ');
    if (splitString.length > 0) {
      splitString.forEach((str: string) => {
        if (str.length > 0) {
          if (queryStr.length > 0) {
            queryStr += ' ';
          }
          queryStr += str.replace(/-/g, '\\-') + '*';
        }
      });
    }

    const url = `${this.oracleUsEquipmentIndexBaseUrl}?searchMode=all${filterClause}&search=${queryStr?.trim() ?? ''}`;

    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<OracleUsIndexSearchResponseDto>(url, { headers: headers });
  }

  public manufacturersFromPtpIndex() {
    if (this.cachedMakes) {
      return of(this.cachedMakes);
    }
    const filter = this.resourceService.getPortalSetting('DeviceDetails_NoDevice_Filter_Manufacturers').Value;
    const url = `${this.ptpEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&facet=Manufacturer,count:3000,sort:value&$count=true&$top=0${filter}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<PtpIndexSearchResponseDto>(url, { headers: headers }).pipe(
      map(resp => {
        this.cachedMakes = resp;
        return resp;
      })
    );
  }

  /**
   * This returns the list of all the Manufacturers that produce the specified
   * type of devices that also have a 'WiFi Only' carrier option.
   *
   * The returned manufacturers will be results from the Oracle US Equipment
   * index.
   *
   * Example return result:
   * [ 'Apple', 'Google', 'Samsung', 'Xiaomi' ]
   *
   * @param deviceType The type of device to find the manufacturers of (like
   * phone, tablet, smart watch, etc...). This is required.
   */
  // and MarketingName ne null - this will remove null models
  public getManufacturersOfWiFiOnlyDevicesFromOracleIndex(deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Attributes/Carrier eq 'Wi-Fi Only' and MarketingName ne null`;
    return this.getMakes(filterClause);
  }

  public getManufacturers(deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and MarketingName ne null`;
    return this.getMakes(filterClause);
  }

  public getManufacturersByDeviceTypeCode(deviceTypeCode: string | null | undefined, filterByDeviceTypeCode = false): Observable<SelectListItem[]> {
    const normalizedDeviceTypeCode = deviceTypeCode?.trim();

    if (!filterByDeviceTypeCode) {
      if (this.cachedApplianceMakes) {
        return of(this.cachedApplianceMakes);
      }

      return this.getMakes(`$filter=ItemGroup eq 'Home Appliance'`).pipe(
        map((makes) => {
          const makeOptions = this.mapToSelectListItems(makes);
          this.cachedApplianceMakes = makeOptions;
          return makeOptions;
        }),
      );
    }

    if (!normalizedDeviceTypeCode) {
      return of([]);
    }

    const cachedMakes = this.cachedMakesByDeviceTypeCode.get(normalizedDeviceTypeCode);
    if (cachedMakes) {
      return of(cachedMakes);
    }

    const filterClause = `$filter=Attributes/DeviceTypeCode eq '${normalizedDeviceTypeCode}' and MarketingName ne null`;

    return this.getMakes(filterClause).pipe(
      map((makes) => {
        const makeOptions = this.mapToSelectListItems(makes);
        this.cachedMakesByDeviceTypeCode.set(normalizedDeviceTypeCode, makeOptions);
        return makeOptions;
      }),
    );
  }

  private mapToSelectListItems(values: string[]): SelectListItem[] {
    return values.map((value) => ({ Key: value, Value: value }));
  }

  private getMakes(filterClause: string): Observable<string[]> {
    const url = `${this.oracleUsEquipmentIndexBaseUrl}?searchMode=all&facet=Manufacturer,count:3000,sort:value&$count=false&$top=0&search=*&${filterClause}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<any>(url, { headers: headers }).pipe(
      map(resp => {
        const facetObjectArray: { Value: string }[] = resp?.Facets?.Manufacturer;
        return Array.from(new Set(facetObjectArray?.map((item) => item?.Value?.trim()).filter((item): item is string => !!item) ?? []));
      }),
    );
  }

  /**
   * This returns the list of all the device models (marketing names) of the given device type
   * that have a wi-fi only carrier option and that are also manufactured by
   * the given manufacturer.
   *
   * The returned models will be results from the Oracle US Equipment
   * index.
   *
   * Example return result:
   * [ 'Galaxy Watch 42', 'Galaxy Watch 46', 'Galaxy Watch Active2 40', 'Galaxy Watch Active2 44 Golf', .... ]
   *
   * @param manufacturer The manufacturer of the devices which you want
   * a list of device models for. This is required.
   *
   * @param deviceType The type of device to find the manufacturers of (like
   * phone, tablet, smart watch, etc...). This is required.
   */
  public getModelsOfWiFiOnlyDevicesForManufacturerFromOracleIndex(manufacturer: string, deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Attributes/Carrier eq 'Wi-Fi Only' and Manufacturer eq '${manufacturer.replace('&', '%26')}'`;
    return this.getModels(filterClause);
  }

  public getAllModels(manufacturer: string, deviceType: EssItemGroup): Observable<string[]> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Manufacturer eq '${manufacturer.replace('&', '%26')}'`;
    return this.getModels(filterClause);
  }

  private getModels(filterClause: string): Observable<string[]> {
    const url = `${this.oracleUsEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&facet=MarketingName,count:3000,sort:value&$count=false&$top=0&search=*&${filterClause}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<any>(url, { headers: headers }).pipe(
      map(resp => {
        const facetObjectArray: { Value: string }[] = resp?.Facets?.MarketingName;
        return facetObjectArray?.map((item) => item?.Value);
      }),
    );
  }

  // TODO: document the comments similar the the above methods
  public findWiFiOnlyDevicesByManufacturerAndModelAndDeviceTypeFromOracleIndex(manufacturer: string, model: string, deviceType: EssItemGroup): Observable<OracleUsIndexSearchResponseDto> {
    const filterClause = `$filter=ItemGroup eq '${deviceType}' and Attributes/Carrier eq 'Wi-Fi Only' and Manufacturer eq '${manufacturer.replace('&', '%26')}' and MarketingName eq '${model}'`;
    const url = `${this.oracleUsEquipmentIndexBaseUrl}?api-version=${this.apiVersion}&searchMode=all&$top=1000&${filterClause}`;
    const headers: HttpHeaders = new HttpHeaders({
      'Ocp-Apim-Subscription-Key': this.apiKey,
    });

    return this.httpClient.get<OracleUsIndexSearchResponseDto>(url, { headers: headers });
  }
}
